import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth';
import Dashboard from './dashboard';
import { SEO, Header, Footer } from '../../../shared/components';
import { route } from '../../../shared/utils';
import { useServices } from '../../../shared/hooks/useServices';
import { ArrowRight, CheckCircle, Zap, FileDown, LayoutGrid, Clock, ChevronRight } from 'lucide-react';

const GOLD = '#FFD93D';

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// Animated plot preview SVG
const PlotAnimation: React.FC = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % 5), 1400);
    return () => clearInterval(t);
  }, []);
  return (
    <svg viewBox="0 0 320 220" style={{ width: '100%', maxWidth: 320 }}>
      {/* Grid background */}
      {Array.from({ length: 7 }).map((_, i) => (
        <line key={`h${i}`} x1="20" y1={20 + i * 30} x2="300" y2={20 + i * 30} stroke="#1a1a1a" strokeWidth="1"/>
      ))}
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={`v${i}`} x1={20 + i * 31} y1="20" x2={20 + i * 31} y2="200" stroke="#1a1a1a" strokeWidth="1"/>
      ))}

      {/* Plot outline — draws in */}
      <rect x="30" y="30" width="260" height="160"
        fill="rgba(255,217,61,0.04)" stroke={GOLD} strokeWidth="2.5"
        strokeDasharray="840" strokeDashoffset={step < 1 ? 840 : 0}
        style={{ transition: 'stroke-dashoffset 1.2s ease' }}
      />

      {/* Room box */}
      {step >= 2 && (
        <rect x="40" y="40" width="80" height="60"
          fill="rgba(255,255,255,0.03)" stroke="#555" strokeWidth="1.5" strokeDasharray="4,3"
          style={{ animation: 'fadeIn 0.5s ease' }}
        />
      )}
      {step >= 2 && <text x="80" y="74" textAnchor="middle" fill="#555" fontSize="9" style={{ animation: 'fadeIn 0.5s ease' }}>Bedroom</text>}

      {/* Dimension arrows + labels */}
      {step >= 3 && <>
        {/* Width arrow */}
        <line x1="30" y1="210" x2="290" y2="210" stroke="#444" strokeWidth="1"/>
        <line x1="30" y1="206" x2="30" y2="214" stroke="#444" strokeWidth="1"/>
        <line x1="290" y1="206" x2="290" y2="214" stroke="#444" strokeWidth="1"/>
        <text x="160" y="208" textAnchor="middle" fill="#666" fontSize="10">40 ft</text>
        {/* Height arrow */}
        <line x1="310" y1="30" x2="310" y2="190" stroke="#444" strokeWidth="1"/>
        <line x1="306" y1="30" x2="314" y2="30" stroke="#444" strokeWidth="1"/>
        <line x1="306" y1="190" x2="314" y2="190" stroke="#444" strokeWidth="1"/>
        <text x="312" y="114" textAnchor="middle" fill="#666" fontSize="10" transform="rotate(90, 312, 114)">20 ft</text>
      </>}

      {/* North indicator */}
      {step >= 4 && (
        <g transform="translate(260, 50)" style={{ animation: 'fadeIn 0.5s ease' }}>
          <circle cx="0" cy="0" r="14" fill="none" stroke="#2a2a2a" strokeWidth="1.5"/>
          <polygon points="0,-10 -5,6 0,2 5,6" fill={GOLD}/>
          <text x="0" y="22" textAnchor="middle" fill="#666" fontSize="9">N</text>
        </g>
      )}

      {/* Scale label */}
      {step >= 4 && (
        <text x="30" y="17" fill="#444" fontSize="8" style={{ animation: 'fadeIn 0.5s ease' }}>Scale 1:100</text>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </svg>
  );
};

// Stat counter
const StatCard: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 32, fontWeight: 800, color: GOLD, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{label}</div>
  </div>
);

const steps = [
  { n: '01', title: 'Pick a Plot Type', desc: 'Choose from regular, irregular, with or without room placement.' },
  { n: '02', title: 'Enter Dimensions', desc: 'Fill in width, depth, setbacks and room sizes in feet or metres.' },
  { n: '03', title: 'Download PDF', desc: 'Get a scaled, print-ready PDF of your plot plan instantly.' },
];

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const { startingPrice } = useServices();
  if (isAuthenticated) return <Dashboard />;

  return (
    <>
      <SEO title={`${import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners'} - Plot Design Platform`} description="Generate professional plot plans in seconds. No specialized software needed." />

      <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff', fontFamily: 'inherit', overflowX: 'hidden' }}>

        <Header />

        {/* ── HERO ── */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '40px 20px 56px' : '72px 32px 80px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 56, alignItems: 'center' }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,217,61,0.08)', border: '1px solid rgba(255,217,61,0.2)',
              borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 600, color: GOLD,
              marginBottom: 24,
            }}>
              <Zap size={12} /> Built for Indian Architects & Engineers
            </div>

            <h1 style={{ fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 800, lineHeight: 1.1, margin: '0 0 20px' }}>
              Instant Plot Layouts,<br />
              <span style={{ color: GOLD }}>Without</span><br />
              Complexity
            </h1>

            <p style={{ fontSize: 16, color: '#777', lineHeight: 1.7, margin: '0 0 8px', maxWidth: 440 }}>
              Create professional, scaled drawings for presentations and planning — faster than ever.
            </p>
            <p style={{ fontSize: 13, color: '#444', lineHeight: 1.6, margin: '0 0 32px', maxWidth: 440, fontStyle: 'italic' }}>
              For design reference only. Not for statutory approvals.
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Link to={route('/auth/register')} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 26px', borderRadius: 9, fontSize: 14, fontWeight: 700,
                background: GOLD, color: '#111', textDecoration: 'none',
              }}>
                Start Free <ArrowRight size={15} />
              </Link>
              <Link to={route('/auth/login')} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '13px 26px', borderRadius: 9, fontSize: 14, fontWeight: 600,
                background: 'transparent', color: '#ccc', textDecoration: 'none',
                border: '1px solid #2a2a2a',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#444'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = '#2a2a2a'; }}
              >
                Sign In
              </Link>
            </div>

            {/* Trust badges */}
            <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
              {['Scaled Output', 'PDF Export'].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555' }}>
                  <CheckCircle size={13} style={{ color: GOLD }} /> {t}
                </div>
              ))}
            </div>
          </div>

          {/* Live plot animation */}
          <div style={{
            background: '#111', border: '1px solid #1e1e1e', borderRadius: 16,
            padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center',
            boxShadow: '0 0 60px rgba(255,217,61,0.04)',
          }}>
            <div style={{ fontSize: 11, color: '#444', marginBottom: 16, letterSpacing: 1, textTransform: 'uppercase' }}>
              Live Preview
            </div>
            <PlotAnimation />
            <div style={{
              display: 'flex', gap: 8, marginTop: 20, width: '100%',
            }}>
              <div style={{ flex: 1, background: '#1a1a1a', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>Width</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: GOLD }}>40 ft</div>
              </div>
              <div style={{ flex: 1, background: '#1a1a1a', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>Depth</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: GOLD }}>20 ft</div>
              </div>
              <div style={{ flex: 1, background: '#1a1a1a', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: '#555', marginBottom: 3 }}>Area</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#ccc' }}>800 sq ft</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── STATS STRIP ── */}
        <div style={{ borderTop: '1px solid #141414', borderBottom: '1px solid #141414', background: '#0d0d0d' }}>
          <div style={{
            maxWidth: 800, margin: '0 auto', padding: '36px 32px',
            display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 16 : 24,
          }}>
            <StatCard value="4" label="Plot Types" />
            <StatCard value={startingPrice !== null ? `₹${startingPrice}` : '—'} label="Starting Price" />
            <StatCard value="PDF" label="Export Format" />
            <StatCard value="< 1min" label="Generation Time" />
          </div>
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '48px 20px' : '80px 32px' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div style={{ fontSize: 11, color: GOLD, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
              How It Works
            </div>
            <h2 style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>Three steps to your plot plan</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            {steps.map((s, i) => (
              <div key={s.n} style={{
                background: '#0f0f0f', border: '1px solid #1e1e1e', borderRadius: 16,
                padding: '28px 24px', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: 16, right: 20,
                  fontSize: 48, fontWeight: 900, color: '#161616', lineHeight: 1,
                }}>
                  {s.n}
                </div>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: i === 2 ? GOLD : 'rgba(255,217,61,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 16,
                }}>
                  {i === 0 && <LayoutGrid size={18} style={{ color: GOLD }} />}
                  {i === 1 && <Clock size={18} style={{ color: GOLD }} />}
                  {i === 2 && <FileDown size={18} style={{ color: '#111' }} />}
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px', color: '#fff' }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>{s.desc}</p>
                {i < 2 && (
                  <ChevronRight size={20} style={{
                    position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)',
                    color: '#2a2a2a', zIndex: 2,
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── PLOT TYPES SHOWCASE ── */}
        <div style={{ background: '#0d0d0d', borderTop: '1px solid #141414' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '48px 20px' : '80px 32px' }}>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, color: GOLD, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
                Plot Types
              </div>
              <h2 style={{ fontSize: 36, fontWeight: 700, margin: 0 }}>Every layout you need</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 14 }}>
              {[
                {
                  label: 'Regular Plot', sub: 'Standard rectangular plot',
                  svg: <svg viewBox="0 0 100 70" width="100" height="70">
                    <rect x="5" y="5" width="90" height="60" fill="rgba(255,217,61,0.05)" stroke={GOLD} strokeWidth="2"/>
                    <text x="50" y="38" textAnchor="middle" fill="#555" fontSize="9">40 × 20</text>
                  </svg>
                },
                {
                  label: 'With Room', sub: 'Plot + room placement',
                  svg: <svg viewBox="0 0 100 70" width="100" height="70">
                    <rect x="5" y="5" width="90" height="60" fill="rgba(255,217,61,0.05)" stroke={GOLD} strokeWidth="2"/>
                    <rect x="11" y="11" width="32" height="22" fill="rgba(255,255,255,0.03)" stroke="#555" strokeWidth="1.2" strokeDasharray="3,2"/>
                    <text x="27" y="25" textAnchor="middle" fill="#444" fontSize="7">Room</text>
                    <text x="50" y="54" textAnchor="middle" fill="#555" fontSize="8">40 × 20</text>
                  </svg>
                },
                {
                  label: 'Irregular Plot', sub: 'Non-rectangular boundaries',
                  svg: <svg viewBox="0 0 100 70" width="100" height="70">
                    <polygon points="20,5 90,5 95,65 5,65" fill="rgba(255,217,61,0.05)" stroke={GOLD} strokeWidth="2"/>
                    <text x="52" y="40" textAnchor="middle" fill="#555" fontSize="14">≠</text>
                  </svg>
                },
                {
                  label: 'Irregular + Room', sub: 'Irregular with room',
                  svg: <svg viewBox="0 0 100 70" width="100" height="70">
                    <polygon points="20,5 90,5 95,65 5,65" fill="rgba(255,217,61,0.05)" stroke={GOLD} strokeWidth="2"/>
                    <rect x="22" y="11" width="28" height="18" fill="rgba(255,255,255,0.03)" stroke="#555" strokeWidth="1.2" strokeDasharray="3,2"/>
                    <text x="36" y="23" textAnchor="middle" fill="#444" fontSize="7">Room</text>
                  </svg>
                },
              ].map(({ label, sub, svg }) => (
                <div key={label} style={{
                  background: '#111', border: '1px solid #1e1e1e', borderRadius: 12,
                  padding: '20px 16px', textAlign: 'center',
                  transition: 'border-color 0.2s',
                }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,217,61,0.3)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '#1e1e1e')}
                >
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>{svg}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#ddd', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 11, color: '#555' }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA BANNER ── */}
        <div style={{ maxWidth: 960, margin: '0 auto', padding: isMobile ? '48px 20px' : '80px 32px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1600 0%, #111 60%)',
            border: '1px solid rgba(255,217,61,0.2)', borderRadius: 20,
            padding: isMobile ? '36px 24px' : '56px 48px', textAlign: 'center',
            boxShadow: '0 0 80px rgba(255,217,61,0.05)',
          }}>
            <h2 style={{ fontSize: 36, fontWeight: 800, margin: '0 0 14px' }}>
              Ready to design your plot?
            </h2>
            <p style={{ fontSize: 15, color: '#777', margin: '0 0 32px' }}>
              Sign up free and get ₹500 in credits to start generating plot plans today.
            </p>
            <Link to={route('/auth/register')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 32px', borderRadius: 10, fontSize: 15, fontWeight: 700,
              background: GOLD, color: '#111', textDecoration: 'none',
            }}>
              Create Free Account <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <Footer />

      </div>
    </>
  );
};

export default HomePage;
